/**
 * Modelo pronto: Consulta de CEP com IA + HTTP ViaCEP.
 * Espelha o fluxo demo testado (IA coleta → consulta → IA resultado).
 */

const AI_COLLECT_PROMPT = `Você é o assistente desta etapa do fluxo UltraWhats.

MISSÃO ÚNICA: coletar o CEP do cliente (8 dígitos, Brasil).

REGRAS OBRIGATÓRIAS:
- Na primeira resposta, mesmo que o cliente só cumprimente, responda ao cumprimento em UMA frase curta e peça o CEP na mesma mensagem.
- Nunca responda apenas "como posso ajudar?", "em que posso ajudar?" ou ofertas genéricas sem pedir o CEP.
- Aceite CEP com ou sem hífen (01310-100 ou 01310100).
- Confirme o CEP com o cliente antes de encerrar esta etapa.
- Quando o cliente confirmar que o CEP está correto, agradeça brevemente e encerre — o sistema consultará o endereço automaticamente.
- Não invente endereço nem bairro; só colete o CEP.
- Seja breve e objetivo (máximo 2 frases por mensagem).`;

const AI_RESULT_PROMPT = `Você é um assistente amigável.

O sistema já consultou o CEP e encontrou:
- Bairro: \${bairro}
- Cidade: \${cidade}
- UF: \${uf}
- Logradouro: \${logradouro}

Informe ao cliente de forma clara e natural o bairro e a cidade onde o CEP está localizado.
Mencione o logradouro se estiver disponível.
Pergunte se deseja ajuda com mais alguma coisa.
Não invente dados além dos fornecidos acima.`;

function buildOpenAiIntegration(name, prompt, objective, autoCompleteOnObjective = false) {
  return {
    name,
    prompt,
    model: "gpt-4o-mini",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    ttsProvider: "azure",
    ttsModel: "tts-1",
    openaiTtsInstructions: "",
    ttsSpeed: 1,
    maxTokens: 600,
    temperature: 0.3,
    apiKey: "",
    maxMessages: 10,
    queueId: 0,
    delay: 5,
    flowMode: "temporary",
    maxInteractions: 8,
    completionTimeout: 15,
    continueKeywords: ["continuar", "próximo", "avançar"],
    objective,
    autoCompleteOnObjective,
    departmentTransfers: [],
    userTransfers: [],
    tagsAllowedForAI: [],
    lanesAllowedForAI: [],
    googleCalendarIntegrationId: 0,
    queueIdAfterCalendarCreate: 0,
    queueIdAfterCalendarUpdate: 0,
    queueIdAfterCalendarCancel: 0,
    sendAudioTranscriptionToContact: false,
    sendLinksAsTextWhenVoice: false,
    assetsAllowedForAI: [],
    maxAttachmentsPerReply: 2,
    provider: "openai",
  };
}

const N = {
  start: "tplCepStart01",
  msgIntro: "tplCepIntro02",
  aiCep: "tplCepAiColeta03",
  msgWait: "tplCepWait04",
  httpCep: "tplCepHttp05",
  cond: "tplCepCond06",
  aiResult: "tplCepAiResult07",
  msgInvalid: "tplCepInvalid08",
  msgHttpErr: "tplCepHttpErr09",
};

const edge = (source, target, sourceHandle, targetHandle) => ({
  id: `xy-edge__${source}${sourceHandle || ""}-${target}${targetHandle || ""}`,
  type: "buttonedge",
  source,
  target,
  ...(sourceHandle ? { sourceHandle } : {}),
  ...(targetHandle ? { targetHandle } : {}),
});

const consultaCepComIaTemplate = {
  nodes: [
    {
      id: N.start,
      type: "start",
      position: { x: 0, y: 120 },
      data: { label: "Início" },
    },
    {
      id: N.msgIntro,
      type: "message",
      position: { x: 220, y: 120 },
      data: {
        label:
          "Olá! 👋 Vou te ajudar a descobrir o *bairro* de um CEP.\n\nEm instantes um assistente vai pedir seu CEP.",
      },
    },
    {
      id: N.aiCep,
      type: "openai",
      position: { x: 480, y: 120 },
      data: {
        typebotIntegration: buildOpenAiIntegration(
          "Assistente CEP",
          AI_COLLECT_PROMPT,
          "Coletar o CEP válido do cliente (8 dígitos) e obter confirmação explícita de que está correto.",
          true
        ),
        timeoutEnabled: true,
        timeoutMinutes: 5,
        timeoutMessage: "Oi, ainda está aí? Preciso do seu CEP para consultar o bairro.",
      },
    },
    {
      id: N.msgWait,
      type: "message",
      position: { x: 740, y: 120 },
      data: { label: "Consultando seu CEP... ⏳" },
    },
    {
      id: N.httpCep,
      type: "httpRequest",
      position: { x: 1000, y: 120 },
      data: {
        url: "https://viacep.com.br/ws/${cep}/json/",
        method: "GET",
        headersString: "",
        requestBody: "",
        headers: "",
        body: "",
        responseVar: "consultaCep",
        timeout: 15000,
        saveVariables: [
          { path: "bairro", variable: "bairro" },
          { path: "localidade", variable: "cidade" },
          { path: "uf", variable: "uf" },
          { path: "logradouro", variable: "logradouro" },
          { path: "erro", variable: "erro" },
        ],
        responseVariables: [
          { path: "bairro", variableName: "bairro" },
          { path: "localidade", variableName: "cidade" },
          { path: "uf", variableName: "uf" },
          { path: "logradouro", variableName: "logradouro" },
          { path: "erro", variableName: "erro" },
        ],
      },
    },
    {
      id: N.cond,
      type: "condition",
      position: { x: 1260, y: 120 },
      data: {
        key: "erro",
        condition: 1,
        value: "true",
      },
    },
    {
      id: N.aiResult,
      type: "openai",
      position: { x: 1540, y: 20 },
      data: {
        typebotIntegration: buildOpenAiIntegration(
          "Resultado CEP",
          AI_RESULT_PROMPT,
          "Informar bairro e cidade do CEP consultado e encerrar cordialmente.",
          true
        ),
        timeoutEnabled: true,
        timeoutMinutes: 5,
        timeoutMessage: "Posso ajudar com mais alguma coisa sobre o endereço?",
      },
    },
    {
      id: N.msgInvalid,
      type: "message",
      position: { x: 1540, y: 200 },
      data: {
        label:
          "Não encontramos esse CEP. 😕\n\nVerifique os números e tente novamente mais tarde ou fale com um atendente.",
      },
    },
    {
      id: N.msgHttpErr,
      type: "message",
      position: { x: 1260, y: 320 },
      data: {
        label:
          "Tivemos um problema técnico ao consultar o CEP. Por favor, tente novamente em alguns minutos.",
      },
    },
  ],
  connections: [
    edge(N.start, N.msgIntro),
    edge(N.msgIntro, N.aiCep),
    edge(N.aiCep, N.msgWait, "a"),
    edge(N.msgWait, N.httpCep, "a", "httpRequest-in"),
    edge(N.httpCep, N.cond, "a"),
    edge(N.httpCep, N.msgHttpErr, "b"),
    edge(N.cond, N.msgInvalid, "a"),
    edge(N.cond, N.aiResult, "b"),
  ],
};

export default consultaCepComIaTemplate;
